import { ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline"
import React, { useState, useEffect } from "react"
import { useMutation } from "urql"

import { Button } from "../../../../components/shared/Buttons"
import { FilePicker } from "../../../../components/shared/FileStackWidgets"
import { Input, Select, Toggle } from "../../../../components/shared/Inputs"
import Typography from "../../../../components/shared/Typography"
import { useToast } from "../../../../contexts/ToastContext"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"

const HeaderForm = ({ theme, practice, onUpdate, onClose }) => {
  const { pages, practice: contextPractice } = useWebsiteBuilderV2()

  // Use practice from context if available, otherwise use the prop
  const currentPractice = contextPractice || practice

  // Dynamic page options from context
  const pageOptions = pages
    ?.filter((page) => page.slug !== "home") // Exclude home page
    ?.map((page) => ({
      value: page.slug,
      label: page.title
    })) || [
    { value: "services", label: "Services" },
    { value: "about", label: "About" },
    { value: "contact", label: "Contact" }
  ]

  const [logo, setLogo] = useState(theme?.header?.logo || null)
  const [logoSize, setLogoSize] = useState(theme?.header?.logoSize || 50)
  const [ctaType, setCtaType] = useState(theme?.header?.ctaType || "page")
  const [buttonText, setButtonText] = useState(theme?.header?.buttonText || "Book Now")
  // Find the correct page slug from stored value or use first available page
  const getInitialPage = () => {
    const storedPage = theme?.header?.page
    if (storedPage) {
      // Check if the stored page exists in current page options
      const matchingPage = pageOptions.find((option) => option.value === storedPage)
      if (matchingPage) {
        return storedPage
      }
    }
    // Fall back to first available page (Services)
    return pageOptions[0]?.value || "services"
  }

  const [page, setPage] = useState(getInitialPage())

  // Promo banner fields
  const [showPromoBanner, setShowPromoBanner] = useState(theme?.header?.showPromoBanner || false)
  const [promoHeadline, setPromoHeadline] = useState(theme?.header?.promoHeadline || "")
  const [promoCtaType, setPromoCtaType] = useState(theme?.header?.promoCtaType || "service")
  const [promoButtonText, setPromoButtonText] = useState(theme?.header?.promoButtonText || "Learn more")
  const [promoService, setPromoService] = useState(theme?.header?.promoService || "")
  const [promoEvent, setPromoEvent] = useState(theme?.header?.promoEvent || "")
  const [showHome, setShowHome] = useState(theme?.header?.showHome || false)

  // GraphQL mutation for uploading images
  const UPLOAD_IMAGE_MUTATION = `
    mutation UploadImage($handle: String!, $description: String!, $provider: String) {
      uploadImage(handle: $handle, description: $description, provider: $provider) {
        result
        errors
        photoUrl
        altText
      }
    }
  `
  const [{ fetching: uploadFetching }, uploadImage] = useMutation(UPLOAD_IMAGE_MUTATION)
  const { showToast } = useToast()

  // Service options from practice
  const serviceOptions =
    currentPractice?.services?.map((service) => ({
      value: service.id.toString(),
      label: service.name
    })) || []

  // Event options from practice (using bookableEventTypes only)
  const eventOptions =
    currentPractice?.bookableEventTypes?.map((eventType) => ({
      value: eventType.slug,
      label: eventType.name,
      url: `/events/${eventType.slug}` // Add the URL for the event type
    })) || []

  // Sync form state with theme prop when it changes
  useEffect(() => {
    setLogo(theme?.header?.logo || null)
    setLogoSize(theme?.header?.logoSize || 50)
    setCtaType(theme?.header?.ctaType || "page")
    setButtonText(theme?.header?.buttonText || "Book Now")

    // Handle page selection with proper mapping - only update if theme actually changed
    const storedPage = theme?.header?.page
    if (storedPage) {
      const matchingPage = pageOptions.find((option) => option.value === storedPage)
      if (matchingPage) {
        setPage(storedPage)
      } else {
        // If stored page doesn't match current options, use first available (Services)
        setPage(pageOptions[0]?.value || "services")
      }
    } else {
      setPage(pageOptions[0]?.value || "services")
    }

    setShowPromoBanner(theme?.header?.showPromoBanner || false)
    setPromoHeadline(theme?.header?.promoHeadline || "")
    setPromoCtaType(theme?.header?.promoCtaType || "service")
    setPromoButtonText(theme?.header?.promoButtonText || "Learn more")
    setPromoService(theme?.header?.promoService || "")
    setPromoEvent(theme?.header?.promoEvent || "")
    setShowHome(theme?.header?.showHome || false)
  }, [theme]) // Removed pageOptions dependency to prevent overriding user selection

  // Handle initial page selection when pageOptions are first loaded
  useEffect(() => {
    if (!page) {
      setPage(pageOptions[0]?.value || "services")
    }
  }, [page])

  // Auto-select first option when ctaType changes
  useEffect(() => {
    if (ctaType === "page" && pageOptions.length > 0) {
      setPage(pageOptions[0].value)
    } else if (ctaType === "service" && serviceOptions.length > 0) {
      setPromoService(serviceOptions[0].value)
    }
  }, [ctaType])

  // Auto-select first option when promoCtaType changes
  useEffect(() => {
    if (promoCtaType === "page" && pageOptions.length > 0) {
      setPromoService(pageOptions[0].value)
    } else if (promoCtaType === "service" && serviceOptions.length > 0) {
      setPromoService(serviceOptions[0].value)
    } else if (promoCtaType === "event" && eventOptions.length > 0) {
      setPromoEvent(eventOptions[0].value)
    }
  }, [promoCtaType])

  // Auto-select first event when switching to "event" type and there's only one event
  useEffect(() => {
    if (promoCtaType === "event" && eventOptions.length === 1 && !promoEvent) {
      setPromoEvent(eventOptions[0].value)
    }
  }, [promoCtaType, eventOptions, promoEvent])

  // Apply temporary preview when values change
  useEffect(() => {
    const headerData = {
      header: {
        logo,
        logoSize,
        ctaType,
        buttonText,
        page,
        showPromoBanner,
        promoHeadline,
        promoCtaType,
        promoButtonText,
        promoService,
        promoEvent,
        showHome,
        // Map form data to the correct settings structure
        ctaAction: ctaType,
        ctaTarget: ctaType === "service" ? promoService : page
      }
    }
    onUpdate(headerData, { temporary: true })
  }, [
    logo,
    logoSize,
    ctaType,
    buttonText,
    page,
    showPromoBanner,
    promoHeadline,
    promoCtaType,
    promoButtonText,
    promoService,
    promoEvent,
    showHome
  ])

  // Handle logo upload
  const handleLogoUpload = (res) => {
    const file = res.filesUploaded[0]
    uploadImage({
      handle: file.handle,
      description: "header_logo",
      provider: "filestack"
    }).then((result) => {
      if (result?.data?.uploadImage?.result === "success") {
        setLogo(result.data.uploadImage.photoUrl)
        showToast("Logo uploaded successfully!")
      } else {
        console.error(result) // eslint-disable-line no-console
        let errorMessage = "There was an error uploading your logo. Please try again later or contact support."
        if (result.data?.uploadImage?.errors) errorMessage += ` ${result.data.uploadImage.errors}`
        showToast({
          type: "error",
          content: errorMessage
        })
      }
    })
  }

  // Handle logo removal
  const handleLogoRemove = () => {
    setLogo(null)
    showToast("Logo removed.")
  }

  const handleSave = () => {
    const saveData = {
      header: {
        logo,
        logoSize,
        ctaType,
        buttonText,
        page,
        showPromoBanner,
        promoHeadline,
        promoCtaType,
        promoButtonText,
        promoService,
        promoEvent,
        showHome,
        // Map form data to the correct settings structure
        ctaAction: ctaType,
        ctaTarget: ctaType === "service" ? promoService : page
      }
    }
    onUpdate(saveData)
    onClose()
  }

  const handleClose = () => {
    // Revert to stored theme when closing without saving
    const storedCtaType = theme?.header?.ctaType || "page"
    const storedPage = theme?.header?.page || pageOptions[0]?.value || "services"
    const storedPromoService = theme?.header?.promoService || ""
    const storedPromoEvent = theme?.header?.promoEvent || ""

    const storedHeaderData = {
      header: {
        logo: theme?.header?.logo || null,
        logoSize: theme?.header?.logoSize || 50,
        ctaType: storedCtaType,
        buttonText: theme?.header?.buttonText || "Book Now",
        page: storedPage,
        showPromoBanner: theme?.header?.showPromoBanner || false,
        promoHeadline: theme?.header?.promoHeadline || "",
        promoCtaType: theme?.header?.promoCtaType || "service",
        promoButtonText: theme?.header?.promoButtonText || "Learn more",
        promoService: storedPromoService,
        promoEvent: storedPromoEvent,
        showHome: theme?.header?.showHome || false,
        // Map form data to the correct settings structure
        ctaAction: storedCtaType,
        ctaTarget: storedCtaType === "service" ? storedPromoService : storedPage
      }
    }
    onUpdate(storedHeaderData, { temporary: true })
    onClose()
  }

  return (
    <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl">
      {/* Header Title and Close */}
      <div className="flex items-center justify-between border-b border-gray-light px-8 py-6">
        <Typography variant="h4" className="font-bold text-gray-dark">
          Header
        </Typography>
        <button onClick={handleClose} className="rounded p-1 transition-colors hover:bg-gray-light">
          <XMarkIcon className="h-6 w-6 text-gray" />
        </button>
      </div>

      {/* Logo Upload */}
      <div className="px-8 pt-6">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Logo
        </Typography>
        <Typography variant="small" className="mb-4 text-gray">
          Upload a logo for your practice. This will be used as the logo for your website
        </Typography>

        {/* Logo Preview */}
        <div className="mb-6 flex items-center justify-center">
          <div
            className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-4"
            style={{
              width: `${logoSize * 2}px`,
              height: `${logoSize * 1.2}px`,
              minWidth: "80px",
              minHeight: "48px"
            }}>
            {logo ? (
              <img
                src={logo}
                alt="Logo"
                className="max-h-full max-w-full object-contain"
                style={{ maxWidth: `${logoSize}px` }}
              />
            ) : (
              <Typography variant="body" className="font-medium text-gray-600">
                {practice?.name || ""}
              </Typography>
            )}
          </div>
        </div>

        {/* Logo Upload Area */}
        {logo ? (
          <div className="mb-6 flex items-center justify-center">
            <div className="flex items-center gap-4">
              <img
                src={logo}
                alt="Header logo"
                className="h-16 w-16 rounded-lg object-contain"
                style={{ maxWidth: `${logoSize}px` }}
              />
              <div className="flex flex-col gap-2">
                <FilePicker
                  as="button"
                  className="font-bold text-teal"
                  onUploadDone={handleLogoUpload}
                  disabled={uploadFetching}
                  transformations={{}}>
                  Edit logo
                </FilePicker>
                <button className="font-bold text-red" onClick={handleLogoRemove} disabled={uploadFetching}>
                  Remove
                </button>
              </div>
            </div>
          </div>
        ) : (
          <FilePicker
            as="button"
            onUploadDone={handleLogoUpload}
            disabled={uploadFetching}
            transformations={{}}
            className="mb-6 flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-ultralight py-6 text-teal">
            <ArrowUpTrayIcon className="mb-2 h-8 w-8" />
            <Typography variant="body" className="mb-1">
              Drag &apos;n&apos; drop or upload an image
            </Typography>
            <Typography variant="small" className="text-gray">
              Supported formats: PNG, JPG, SVG
            </Typography>
          </FilePicker>
        )}

        {/* Logo Size Slider */}
        <div className="mb-8">
          <Typography variant="h5" className="mb-1 font-medium text-gray-dark">
            Logo size
          </Typography>
          <Typography variant="small" className="mb-3 text-gray">
            Use the slider to adjust the logo size within the header.
          </Typography>
          <div className="flex items-center space-x-3">
            <input
              type="range"
              min="72"
              max="150"
              value={logoSize}
              onChange={(e) => setLogoSize(parseInt(e.target.value))}
              className="h-2 flex-1 cursor-pointer appearance-none rounded-lg bg-gray-200 slider"
            />
            <Typography variant="small" className="min-w-[3rem] font-medium text-gray-dark">
              {logoSize}px
            </Typography>
          </div>
        </div>
      </div>

      {/* Navigation Settings */}
      <div className="px-8 pb-8">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Navigation
        </Typography>
        <Typography variant="small" className="mb-4 text-gray">
          Configure which navigation links to show in the header.
        </Typography>
        <div className="mb-6 flex items-center justify-between">
          <div className="flex-1">
            <Typography variant="body" className="font-medium text-gray-dark">
              Show [Home] link
            </Typography>
            <Typography variant="small" className="text-gray">
              Include a [Home] link in the navigation menu
            </Typography>
          </div>
          <div className="ml-4">
            <Toggle checked={showHome} onChange={setShowHome} />
          </div>
        </div>
      </div>

      {/* CTA Button Section */}
      <div className="px-8 pb-8">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Call-to-action button
        </Typography>
        <Typography variant="small" className="mb-4 text-gray">
          Guide visitors to the next step with a clear call-to-action button.
        </Typography>
        <div className="mb-4 flex items-center space-x-6">
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              name="ctaType"
              value="page"
              checked={ctaType === "page"}
              onChange={() => setCtaType("page")}
              className="h-5 w-5 border-gray-300 text-teal form-radio focus:ring-teal"
            />
            <span className="font-medium text-gray-dark">Page</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              name="ctaType"
              value="service"
              checked={ctaType === "service"}
              onChange={() => setCtaType("service")}
              className="h-5 w-5 border-gray-300 text-teal form-radio focus:ring-teal"
            />
            <span className="font-medium text-gray-dark">Service</span>
          </label>
        </div>
        <Input
          label="Button text"
          value={buttonText}
          onChange={(e) => setButtonText(e.target.value)}
          className="mb-4"
        />
        {ctaType === "page" ? (
          <Select label="Page" value={page} onChange={(e) => setPage(e.target.value)} options={pageOptions} />
        ) : (
          <Select
            label="Service"
            value={promoService}
            onChange={(e) => setPromoService(e.target.value)}
            options={serviceOptions}
            placeholder="Select a service"
          />
        )}
      </div>

      {/* Promo Banner Section */}
      <div className="px-8 pb-8">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex-1">
            <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
              Promo Banner
            </Typography>
            <Typography variant="small" className="text-gray">
              Add a small promo banner above the header that is visible on all pages. Use this to promote a specific
              service, package, event, or any action you want to promote.
            </Typography>
          </div>
          <div className="ml-4">
            <Toggle checked={showPromoBanner} onChange={setShowPromoBanner} />
          </div>
        </div>

        {showPromoBanner && (
          <div className="space-y-6">
            {/* Promo Headline */}
            <div>
              <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
                Promo headline
              </Typography>
              <Typography variant="small" className="mb-3 text-gray">
                Describe your promotion in a short headline.
              </Typography>
              <textarea
                value={promoHeadline}
                onChange={(e) => setPromoHeadline(e.target.value)}
                placeholder="Enter your promo headline..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
                rows={3}
              />
            </div>

            {/* Promo CTA Section */}
            <div>
              <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
                Promo call-to-action link
              </Typography>
              <Typography variant="small" className="mb-4 text-gray">
                Guide visitors to the next step with a clear call-to-action button.
              </Typography>

              {/* CTA Type Radio Buttons */}
              <div className="mb-4 flex items-center space-x-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="promoCtaType"
                    value="page"
                    checked={promoCtaType === "page"}
                    onChange={() => setPromoCtaType("page")}
                    className="h-5 w-5 border-gray-300 text-teal form-radio focus:ring-teal"
                  />
                  <span className="font-medium text-gray-dark">Page</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="promoCtaType"
                    value="service"
                    checked={promoCtaType === "service"}
                    onChange={() => setPromoCtaType("service")}
                    className="h-5 w-5 border-gray-300 text-teal form-radio focus:ring-teal"
                  />
                  <span className="font-medium text-gray-dark">Service</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="promoCtaType"
                    value="event"
                    checked={promoCtaType === "event"}
                    onChange={() => setPromoCtaType("event")}
                    className="h-5 w-5 border-gray-300 text-teal form-radio focus:ring-teal"
                  />
                  <span className="font-medium text-gray-dark">Events</span>
                </label>
              </div>

              {/* Button Text */}
              <Input
                label="Button text"
                value={promoButtonText}
                onChange={(e) => setPromoButtonText(e.target.value)}
                className="mb-4"
              />

              {/* Page Selection (only when page type is selected) */}
              {promoCtaType === "page" && (
                <Select
                  label="Page"
                  value={promoService}
                  onChange={(e) => setPromoService(e.target.value)}
                  options={pageOptions}
                  placeholder="Select a page"
                />
              )}

              {/* Service Selection (only when service type is selected) */}
              {promoCtaType === "service" && (
                <div>
                  <Select
                    label="Service"
                    value={promoService}
                    onChange={(e) => setPromoService(e.target.value)}
                    options={serviceOptions}
                    placeholder="Select a service"
                  />
                  <Typography variant="small" className="mt-2 text-gray">
                    Go to your Services to add additional options.
                  </Typography>
                </div>
              )}

              {/* Event Selection (only when event type is selected) */}
              {promoCtaType === "event" && (
                <div>
                  <Select
                    label="Event"
                    value={promoEvent}
                    onChange={(e) => setPromoEvent(e.target.value)}
                    options={eventOptions}
                    placeholder={eventOptions.length > 0 ? "Select an event" : "No events available"}
                  />
                  <Typography variant="small" className="mt-2 text-gray">
                    {eventOptions.length > 0
                      ? "Go to your Events to add additional options."
                      : "No events found. Go to your Events section to create event types."}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer Buttons */}
      <div className="flex items-center justify-end space-x-3 border-t border-gray-light bg-white px-8 py-6">
        <Button type="secondary" onClick={handleClose} className="px-6">
          Close
        </Button>
        <Button type="primary" onClick={handleSave} className="px-6">
          Save
        </Button>
      </div>
    </div>
  )
}

export default HeaderForm
